import axios from "axios"
import accessToken from "./jwt-token-access/accessToken"
import axiosRetry from "axios-retry"
import { createHmacSha256, generateStrongKey } from "common/utils"
import { getRetryDelay, shouldRetry } from "utils/retryHandler"
import { trimDeep } from "utils/formUtils"
import { shouldRedirectToForbidden } from "utils/apiForbiddenRedirect"
import { redirectOnApiForbidden } from "utils/redirectOnForbidden"
import { redirectToMaintenance, isMaintenanceResponse } from "utils/maintenanceRedirect"
import { redirectToLoginAfterSessionEnd } from "utils/sessionCleanup"
import { API_TIMEOUT_MS } from "constants/apiTimeout"

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1/"
export const DOMAIN_URL = process.env.REACT_APP_DOMAIN_URL || "http://localhost:3000"

const HMAC_SECRET_KEY = process.env.REACT_APP_HMAC_SECRET_KEY || ""
// const TENANT_HEADER = process.env.REACT_APP_TENANT_HEADER || "X-Tenant-ID"
// const TENANT_STORAGE_KEY = "tenantId"

const axiosApi = axios.create({
  baseURL: API_URL,
  timeout: API_TIMEOUT_MS,
})

axiosApi.interceptors.request.use(
  async function (config) {
    const token = await accessToken()
    if (token) {
      config.headers.Authorization = token
    }

    const language =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("I18N_LANGUAGE")
        : null
    config.headers["Accept-Language"] = language === "sa" ? "ar" : "en"

    const accountScope =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("accountScope")
        : null
    const requestUrl = String(config.url || "")
    const isSuperAdminApi =
      accountScope === "super_admin" || requestUrl.includes("superadmin/")

    if (!isSuperAdminApi) {
      config.headers["X-Tenant-Id"] =
        localStorage.getItem("TENANT_ID") ||
        sessionStorage.getItem("TENANT_ID") ||
        localStorage.getItem("tenantId")
    }

    // Inject tenant header from localStorage
    // const tenantId =
    //   typeof localStorage !== "undefined"
    //     ? localStorage.getItem(TENANT_STORAGE_KEY)
    //     : null
    // if (tenantId) {
    //   config.headers[TENANT_HEADER] = tenantId
    // }

    // Add HMAC signature for PUT, PATCH, POST requests
    const method = config.method ? config.method.toUpperCase() : null
    if (method === "PUT" || method === "PATCH" || method === "POST") {
      try {
        // Trim string fields in request payloads before signing/sending
        if (config.data instanceof FormData) {
          config.data = trimDeep(config.data)
        } else if (config.data && typeof config.data === "object") {
          config.data = trimDeep(config.data)
        } else if (typeof config.data === "string" && config.data.trim) {
          config.data = config.data.trim()
        }

        // Get request body - it could be in data or in the config
        const requestBody = config.data || config.body || {}

        // Convert body to string for hashing
        let bodyString = ""
        if (requestBody instanceof FormData) {
          // For FormData, we need to handle it differently
          // Convert FormData to a string representation
          const formDataEntries = []
          for (const entry of requestBody.entries()) {
            formDataEntries.push(entry[0] + "=" + entry[1])
          }
          bodyString = formDataEntries.join("&")
        } else if (typeof requestBody === "string") {
          bodyString = requestBody
        } else if (requestBody && typeof requestBody === "object") {
          bodyString = JSON.stringify(requestBody)
        }

        // Create HMAC hash only if we have a body string and signing key
        if (bodyString && HMAC_SECRET_KEY) {
          const hash = await createHmacSha256(HMAC_SECRET_KEY, bodyString)
          if (hash) {
            config.headers["X-Signature"] = hash
          }
        }
      } catch (error) {
        console.error("Error generating HMAC signature:", error)
      }
    } else if (method === "GET" || method === "DELETE") {
      try {
        // Generate a strong key for GET/DELETE requests
        const generatedKey = generateStrongKey()

        // Add the generated key to x-st header
        config.headers["x-st"] = generatedKey

        if (HMAC_SECRET_KEY) {
          const hash = await createHmacSha256(HMAC_SECRET_KEY, generatedKey)
          if (hash) {
            config.headers["X-Signature"] = hash
          }
        }
      } catch (error) {
        console.error("Error generating HMAC signature for GET/DELETE:", error)
      }
    }

    return config
  },
  function (error) {
    return Promise.reject(error)
  }
)

axiosApi.interceptors.response.use(
  function (response) {
    return response
  },
  function (error) {
    const status = error && error.response && error.response.status
    const statusCode =
      error &&
      error.response &&
      error.response.data &&
      error.response.data.statusCode

    if (status === 401 || statusCode === 401) {
      redirectToLoginAfterSessionEnd("/login")
    }

    if (
      (status === 403 || statusCode === 403) &&
      shouldRedirectToForbidden(error, error?.config)
    ) {
      redirectOnApiForbidden()
    }

    if (isMaintenanceResponse(error)) {
      redirectToMaintenance(error)
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)

// Applying axios-retry to your axios instance
axiosRetry(axiosApi, {
  retries: parseInt(process.env.REACT_APP_RETRY_ATTEMPTS, 10) || 3,
  retryDelay: getRetryDelay,
  retryCondition: shouldRetry,
})

export function get(url, config) {
  config = config || {}
  return axiosApi.get(url, Object.assign({}, config)).then(function (response) {
    return response.data
  })
}

export function post(url, data, config) {
  config = config || {}
  data = data || {}
  return axiosApi
    .post(url, Object.assign({}, data), Object.assign({}, config))
    .then(function (response) {
      return response.data
    })
    .catch(function (err) {
      const errorData =
        err && err.response && err.response.data ? err.response.data : null
      // If server provided an error response body, return it so callers get a consistent payload
      if (errorData) return errorData
      // If no response (network error, CORS, timeout), rethrow to allow proper rejection handling
      throw err
    })
}

export function put(url, data, config) {
  config = config || {}
  data = data || {}
  return axiosApi
    .put(url, Object.assign({}, data), Object.assign({}, config))
    .then(function (response) {
      return response.data
    })
    .catch(function (err) {
      const errorData =
        err && err.response && err.response.data ? err.response.data : null
      if (errorData) return errorData
      throw err
    })
}

export function del(url, data, config) {
  config = config || {}
  data = data || {}
  return axiosApi
    .delete(url, { data: Object.assign({}, data) }, Object.assign({}, config))
    .then(function (response) {
      return response.data
    })
    .catch(function (err) {
      const errorData =
        err && err.response && err.response.data ? err.response.data : null
      if (errorData) return errorData
      throw err
    })
}

export function patch(url, config) {
  config = config || {}
  return axiosApi
    .patch(url, Object.assign({}, config))
    .then(function (response) {
      return response.data
    })
    .catch(function (err) {
      const errorData =
        err && err.response && err.response.data ? err.response.data : null
      if (errorData) return errorData
      throw err
    })
}

export function formDataPost(url, data, config) {
  config = config || {}
  return axiosApi
    .post(url, data, Object.assign({}, config)) // Directly pass the FormData object
    .then(function (response) {
      return response.data
    })
}

export function formDataPut(url, data, config) {
  config = config || {}
  return axiosApi
    .put(url, data, Object.assign({}, config)) // Directly pass the FormData object
    .then(function (response) {
      return response.data
    })
}

export function pdfDownload(url, data, config) {
  var requestConfig = Object.assign({}, config || {})
  requestConfig.responseType = "blob"
  return axiosApi
    .post(url, data, requestConfig) // Directly pass the FormData object
    .then(function (response) {
      return response.data
    })
}
